import { type FC, memo } from 'react';
import type { ReactNode } from 'react';

import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import type { GraphEdgeSettings } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { selectTopologyPresetDraftGroups } from 'components/Graph/GraphSidePanel/selectors/selectTopologyPresetDraftGroups';
import { selectTopologyPresetDraftOtherProtocols } from 'components/Graph/GraphSidePanel/selectors/selectTopologyPresetDraftOtherProtocols';
import { addEdgeGroup } from 'components/Graph/commands/addEdgeGroup';
import { useLocale } from 'containers/App/LocaleContext';
import {
  GraphProtocolsSettings,
  type SaveSettingsCommand,
} from '../GraphProtocolsSettingsWindow/GraphProtocolsSettings';
import { TopologyLayoutSettingsProtocolsGroup } from '../TopologyLayoutSettingsProtocolsGroup/TopologyLayoutSettingsProtocolsGroup';
import { TopologyLayoutSettingsProtocolsGroupDragLayer } from '../TopologyLayoutSettingsProtocolsGroup/TopologyLayoutSettingsProtocolsGroupDragLayer';
import { TopologyLayoutSettingsProtocolsProtocolDragLayer } from '../TopologyLayoutSettingsProtocolsProtocol/TopologyLayoutSettingsProtocolsProtocolDragLayer';
import type { StpInstancesHook } from '../TopologyLayoutSettingsProtocolsProtocol/useStpInstances';
import { selectAvailableEntities } from '../selectors/selectAvailableEntities';

interface Props {
  closePane: () => void;
  openPane: (paneContent: ReactNode) => void;
  stpInstancesHook: StpInstancesHook;
}

const BareTopologyLayoutSettingsProtocols: FC<Props> = (props) => {
  const msg = useLocale();

  const handleAddGroupClick = () => {
    props.openPane(
      <GraphProtocolsSettings
        presetDraftState={ctxProps.presetDraftState}
        groupId={null}
        onClose={props.closePane}
        onSave={handleAddGroupConfirm}
      />,
    );
  };

  const handleAddGroupConfirm = (command: SaveSettingsCommand) => {
    ctxProps.dispatch(addEdgeGroup, command);
    props.closePane();
  };

  const ctxProps = useGraphContextSelectors({
    availableEntities: selectAvailableEntities,
    presetDraftState: prop('layoutSettings', 'presetDraftState'),
    draggedGroupId: prop('layoutSettings', 'draggedGroupId'),
    dispatch: prop('dispatch'),
    edges: prop('layoutSettings', 'presetDraft', 'edges'),
    presetEditMode: prop('layoutSettings', 'presetEditMode'),
    groups: selectTopologyPresetDraftGroups,
    otherProtocols: selectTopologyPresetDraftOtherProtocols,
    selectedStpInstances: prop(
      'technologies',
      'selectedTechnologies',
      'stpInstances',
    ),
  });

  const filterAvailableProtocols = <T extends GraphEdgeSettings>(
    protocols: T[],
  ): T[] =>
    ctxProps.presetEditMode
      ? protocols
      : protocols.filter((protocol) =>
          ctxProps.availableEntities.edgeSettingsIds.includes(protocol.id),
        );

  const availableOtherProtocols = filterAvailableProtocols(
    ctxProps.otherProtocols,
  );

  return (
    <>
      {ctxProps.presetEditMode && (
        <div className='mb-5 flex justify-between px-5'>
          <Button
            variant='tertiary'
            icon='fa-plus'
            type='button'
            onClick={handleAddGroupClick}
          >
            <span>
              {msg.canvasGraph.graphSidePanel.layoutSettings.btnAddGroup}
            </span>
          </Button>
        </div>
      )}
      <ScrollColumnView className='overflow-menu__container'>
        <div className='mb-5 flex flex-1 flex-col'>
          {/* Drag layer can be only 1 for protocols and 1 for groups because of react-dnd performance issue.
              More drag layers would slow down dragging.
              Here we provide props for group and protocol.
              Other props will be passed inside component from `item` passed from DragLayer.
          */}
          <TopologyLayoutSettingsProtocolsProtocolDragLayer />
          <TopologyLayoutSettingsProtocolsGroupDragLayer />
          {ctxProps.groups.map((group, index) => (
            <TopologyLayoutSettingsProtocolsGroup
              closePane={props.closePane}
              draggedGroupId={ctxProps.draggedGroupId}
              groupId={group.id}
              groupIndex={index}
              groupGrouped={group.grouped}
              groupName={group.name}
              groupVisible={group.visible}
              key={group.id}
              openPane={props.openPane}
              otherProtocols={false}
              protocols={filterAvailableProtocols(group.children)}
              style={group.style}
              stpInstancesHook={props.stpInstancesHook}
            />
          ))}
          {(ctxProps.presetEditMode || availableOtherProtocols.length > 0) && (
            <TopologyLayoutSettingsProtocolsGroup
              closePane={props.closePane}
              draggedGroupId={ctxProps.draggedGroupId}
              groupGrouped={false}
              groupId={null}
              groupName={
                msg.canvasGraph.graphSidePanel.layoutSettings.otherProtocols
              }
              groupVisible
              groupIndex={-1}
              openPane={props.openPane}
              otherProtocols
              protocols={availableOtherProtocols}
              stpInstancesHook={props.stpInstancesHook}
            />
          )}
        </div>
      </ScrollColumnView>
    </>
  );
};

export const TopologyLayoutSettingsProtocols = memo(
  BareTopologyLayoutSettingsProtocols,
);
